import * as wasmCheck from 'wasm-check';
import KinoBackgroundEffect from './KinoBackgroundEffect';
import createTFLiteModule from './vendor/tflite/tflite.js';
import createTFLiteSIMDModule from './vendor/tflite/tflite-simd.js';

/**
 * Available segmentation models.
 *
 * NOTE: only `modelLandscape` is currently bundled in `public/libs`. The lighter Meet models
 * (`segm_lite_v681` / `segm_full_v679`) are listed here for forward-compatibility: drop the
 * matching `.tflite` files into `public/libs` and map the desired tier to them in
 * `selectModelKey()` to use them on low-end devices. Their output is two-channel
 * (background/person logits) and is decoded by the `segmentationType: 'twoChannel'` branch in
 * KinoBackgroundEffect; the landscape model outputs a single person-probability channel.
 */
const models = {
    model96: {
        path: 'libs/segm_lite_v681.tflite',
        width: 160,
        height: 96,
        segmentationType: 'twoChannel'
    },
    model144: {
        path: 'libs/segm_full_v679.tflite',
        width: 256,
        height: 144,
        segmentationType: 'twoChannel'
    },
    modelLandscape: {
        path: 'libs/selfie_segmentation_landscape.tflite',
        width: 256,
        height: 144,
        segmentationType: 'single'
    }
};

/**
 * Per-tier processing budget.
 *
 * `renderFrameRate` drives the (cheap, GPU) compositing cadence, matching the previous capture
 * frame rates. `inferenceFrameRate` throttles the expensive segmentation so the mask is computed
 * less often and reused/temporally smoothed between runs - this is the main CPU saving on low-end
 * devices. `maskSmoothing` is the EMA weight applied to each fresh mask (higher = more responsive,
 * lower = smoother and better at hiding the reduced inference rate).
 */
const PERFORMANCE_BUDGET = {
    low: {
        renderFrameRate: 10,
        inferenceFrameRate: 4,
        maskSmoothing: 0.5,
        blurIterations: 1
    },
    mid: {
        renderFrameRate: 15,
        inferenceFrameRate: 7,
        maskSmoothing: 0.6,
        blurIterations: 2
    },
    high: {
        renderFrameRate: 20,
        inferenceFrameRate: 10,
        maskSmoothing: 0.7,
        blurIterations: 2
    }
};

// Cache downloaded model bytes across effect instances (preview + live + re-toggles) so the model
// is not re-downloaded every time an effect is created.
const modelBufferCache = {};

/**
 * Classifies the device into a performance tier from available hardware hints.
 *
 * @returns {'low'|'mid'|'high'} - The device performance tier.
 */
function getDeviceTier() {
    const cores = (navigator && navigator.hardwareConcurrency) || 4;

    // `navigator.deviceMemory` is only exposed by Chromium; default to a mid value elsewhere.
    const memory = (navigator && navigator.deviceMemory) || 4;

    if (cores <= 4 || memory <= 2) {
        return 'low';
    }
    if (cores <= 8 || memory <= 4) {
        return 'mid';
    }

    return 'high';
}

/**
 * Picks the segmentation model key for the given tier.
 *
 * Currently every tier resolves to the bundled landscape model. Once the lighter models are added
 * to `public/libs`, map 'low' -> 'model96' (and optionally 'mid' -> 'model144') here.
 *
 * @param {'low'|'mid'|'high'} tier - Device performance tier.
 * @returns {string} - Key into {@code models}.
 */
function selectModelKey(tier) { // eslint-disable-line no-unused-vars
    return 'modelLandscape';
}

/**
 * Downloads (and caches) the raw bytes of a tflite model.
 *
 * @param {string} modelPath - Path to the model relative to the app root.
 * @returns {Promise<ArrayBuffer>} - The model bytes.
 */
async function loadModelBuffer(modelPath) {
    if (modelBufferCache[modelPath]) {
        return modelBufferCache[modelPath];
    }

    const modelResponse = await fetch(modelPath);

    if (!modelResponse.ok) {
        throw new Error(`Failed to download tflite model: ${modelPath}`);
    }

    const buffer = await modelResponse.arrayBuffer();

    modelBufferCache[modelPath] = buffer;

    return buffer;
}

/**
 * Creates a new instance of KinoBackgroundEffect. This loads the segmentation model used to
 * extract person segmentation and selects per-device processing parameters.
 *
 * @param {Object} virtualBackground - The virtual object that contains the background image source
 * and the flag/blur value that indicates which effect is activated.
 * @returns {Promise<KinoBackgroundEffect>}
 */
export async function createVirtualBackgroundEffect(virtualBackground) {
    if (!MediaStreamTrack.prototype.getSettings && !MediaStreamTrack.prototype.getConstraints) {
        throw new Error('KinoBackgroundEffect not supported!');
    }

    let tflite;

    if (wasmCheck.feature.simd) {
        tflite = await createTFLiteSIMDModule();
    } else {
        tflite = await createTFLiteModule();
    }

    const tier = getDeviceTier();
    let modelKey = selectModelKey(tier);
    let model = models[modelKey];

    // Load the selected model, falling back to the always-present landscape model if a lighter
    // model file is missing (e.g. not yet deployed). This keeps VBG working on every device.
    let modelBuffer;

    try {
        modelBuffer = await loadModelBuffer(model.path);
    } catch (error) {
        console.warn('Falling back to landscape segmentation model', error);
        modelKey = 'modelLandscape';
        model = models.modelLandscape;
        modelBuffer = await loadModelBuffer(model.path);
    }

    tflite.HEAPU8.set(new Uint8Array(modelBuffer), tflite._getModelBufferMemoryOffset());
    tflite._loadModel(modelBuffer.byteLength);

    const budget = PERFORMANCE_BUDGET[tier];
    const options = {
        width: model.width,
        height: model.height,
        segmentationType: model.segmentationType,
        deviceTier: tier,
        renderFrameRate: budget.renderFrameRate,
        inferenceFrameRate: budget.inferenceFrameRate,
        maskSmoothing: budget.maskSmoothing,
        blurIterations: budget.blurIterations,
        virtualBackground
    };

    return new KinoBackgroundEffect(tflite, options);
}
